import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import './BuyNowPage.css';

export default function BuyNowPage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const planId = params.get('plan') || '';
  // Wizard steps: 1=User Info, 2=Service Address, 3=Payment Info, 4=Number Options, 5=Review & Submit, 6=Success
  const [step, setStep] = useState(1);
  const [userData, setUserData] = useState({ fullName: '', email: '', password: '', confirmPassword: '' });
  const [address, setAddress] = useState({ line1: '', line2: '', city: '', state: '', postalCode: '', country: '' });
  const [payment, setPayment] = useState({ cardNumber: '', expMonth: '', expYear: '', cvv: '' });
  const [numberOption, setNumberOption] = useState('new'); // 'new' or 'port'
  const [portNumbers, setPortNumbers] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);


  // Navigate to previous step
  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };
  // Handle next/review/submit
  const handleNext = async (e) => {
    e.preventDefault();
    setError('');
    // Step-specific validation
    if (step === 1) {
      const { fullName, email, password, confirmPassword } = userData;
      if (!fullName.trim() || !email.trim() || !password || !confirmPassword) {
        setError('Please fill in all fields'); return;
      }
      if (password !== confirmPassword) { setError('Passwords do not match'); return; }
      if (password.length < 8) { setError('Password must be at least 8 characters long'); return; }
      if (!/[A-Z]/.test(password)) { setError('Password must contain at least one uppercase letter'); return; }
      if (!/[a-z]/.test(password)) { setError('Password must contain at least one lowercase letter'); return; }
      if (!/[0-9]/.test(password)) { setError('Password must contain at least one number'); return; }
      if (!/[!@#$%^&*]/.test(password)) { setError('Password must contain at least one special character'); return; }
      setStep(2);
    } else if (step === 2) {
      const { line1, city, state, postalCode, country } = address;
      if (!line1.trim() || !city.trim() || !state.trim() || !postalCode.trim() || !country.trim()) {
        setError('Please fill in required address fields'); return;
      }
      setStep(3);
    } else if (step === 3) {
      const { cardNumber, expMonth, expYear, cvv } = payment;
      if (!cardNumber.trim() || !expMonth.trim() || !expYear.trim() || !cvv.trim()) {
        setError('Please fill in all payment fields'); return;
      }
      setStep(4);
    } else if (step === 4) {
      if (numberOption === 'port' && !portNumbers.trim()) {
        setError('Please enter numbers to port'); return;
      }
      setStep(5);
    } else if (step === 5) {
      // Final submit
      setLoading(true);
      try {
        const payload = { planId, ...userData, address, payment, numberOption, portNumbers };
        const res = await fetch(`${process.env.REACT_APP_API_URL}/buy-now`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Purchase failed');
        setMessage('Purchase successful! Please check your email for confirmation.');
        setStep(6);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // Capitalize plan name for display
  const displayPlan = planId
    ? planId.charAt(0).toUpperCase() + planId.slice(1)
    : '';

  return (
    <div className="buy-now-container">
      <div className="buy-now-card">
        <h2 className="buy-now-title">Buy Now{displayPlan ? ` - ${displayPlan}` : ''}</h2>
        {step <= 5 && <div className="wizard-stepper">Step {step} of 5</div>}
        {error && <p className="error">{error}</p>}
        {step < 6 ? (
          <form onSubmit={handleNext}>
              {step === 1 && (
                <>
                  <div className="field-row">
                    <input className="input-field field-half" type="text" placeholder="Full Name" value={userData.fullName}
                      onChange={e => setUserData({ ...userData, fullName: e.target.value })} required />
                    <input className="input-field field-half" type="email" placeholder="Email" value={userData.email}
                      onChange={e => setUserData({ ...userData, email: e.target.value })} required />
                  </div>
                  <div className="field-row">
                    <input className="input-field field-half" type="password" placeholder="Password" value={userData.password}
                      onChange={e => setUserData({ ...userData, password: e.target.value })} required />
                    <input className="input-field field-half" type="password" placeholder="Confirm Password" value={userData.confirmPassword}
                      onChange={e => setUserData({ ...userData, confirmPassword: e.target.value })} required />
                  </div>
                </>
              )}
              {step === 2 && (
                <>
                  <div className="field-row">
                    <input className="input-field field-full" type="text" placeholder="Address Line 1" value={address.line1}
                      onChange={e => setAddress({ ...address, line1: e.target.value })} required />
                  </div>
                  <div className="field-row">
                    <input className="input-field field-full" type="text" placeholder="Address Line 2 (optional)" value={address.line2}
                      onChange={e => setAddress({ ...address, line2: e.target.value })} />
                  </div>
                  <div className="field-row">
                    <input className="input-field field-half" type="text" placeholder="City" value={address.city}
                      onChange={e => setAddress({ ...address, city: e.target.value })} required />
                    <input className="input-field field-half" type="text" placeholder="State/Province" value={address.state}
                      onChange={e => setAddress({ ...address, state: e.target.value })} required />
                  </div>
                  <div className="field-row">
                    <input className="input-field field-half" type="text" placeholder="Postal Code" value={address.postalCode}
                      onChange={e => setAddress({ ...address, postalCode: e.target.value })} required />
                    <input className="input-field field-half" type="text" placeholder="Country" value={address.country}
                      onChange={e => setAddress({ ...address, country: e.target.value })} required />
                  </div>
                </>
              )}
              {step === 3 && (
                <>
                  <div className="field-row">
                    <input className="input-field field-full" type="text" placeholder="Card Number" value={payment.cardNumber}
                      onChange={e => setPayment({ ...payment, cardNumber: e.target.value })} required />
                  </div>
                  <div className="field-row">
                    <input className="input-field field-third" type="text" placeholder="Exp Month (MM)" value={payment.expMonth}
                      onChange={e => setPayment({ ...payment, expMonth: e.target.value })} required />
                    <input className="input-field field-third" type="text" placeholder="Exp Year (YYYY)" value={payment.expYear}
                      onChange={e => setPayment({ ...payment, expYear: e.target.value })} required />
                    <input className="input-field field-third" type="text" placeholder="CVV" value={payment.cvv}
                      onChange={e => setPayment({ ...payment, cvv: e.target.value })} required />
                  </div>
                </>
              )}
              {step === 4 && (
                <>
                  <div className="field-row radio-group">
                    <label className="radio-item">
                      <input type="radio" name="numberOption" value="new" checked={numberOption === 'new'}
                        onChange={e => setNumberOption(e.target.value)} /> Generate New Number
                    </label>
                    <label className="radio-item">
                      <input type="radio" name="numberOption" value="port" checked={numberOption === 'port'}
                        onChange={e => setNumberOption(e.target.value)} /> Port Existing Number(s)
                    </label>
                  </div>
                  {numberOption === 'port' && (
                    <div className="field-row">
                      <textarea className="input-field field-full" placeholder="Enter numbers to port, comma-separated"
                        value={portNumbers}
                        onChange={e => setPortNumbers(e.target.value)} required />
                    </div>
                  )}
                </>
              )}
              {step === 5 && (
                <div className="wizard-review">
                  <h3>Review Your Order</h3>
                  <p><strong>Plan:</strong> {displayPlan}</p>
                  <p><strong>Full Name:</strong> {userData.fullName}</p>
                  <p><strong>Email:</strong> {userData.email}</p>
                  <p><strong>Address:</strong> {`${address.line1}${address.line2 ? ', '+address.line2 : ''}, ${address.city}, ${address.state} ${address.postalCode}, ${address.country}`}</p>
                  <p><strong>Number Option:</strong> {numberOption === 'new' ? 'Generate New' : `Port: ${portNumbers}`}</p>
                </div>
              )}
              <div className="wizard-actions">
                {step > 1 && <button type="button" onClick={prevStep}>Back</button>}
                <button type="submit" disabled={loading}>
                  {step < 5 ? 'Next' : loading ? 'Processing...' : 'Submit Purchase'}
                </button>
              </div>
          </form>
        ) : (
          <div className="wizard-success">
            <h3>Thank you!</h3>
            <p>{message}</p>
            <button type="button" className="btn-primary" onClick={() => window.location.href = '/'}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
